import { graphql, Link } from 'gatsby'
import {
  renderRichText,
  RenderRichTextData,
} from 'gatsby-source-contentful/rich-text'
import React, { useContext } from 'react'

import BlogPostList from '../components/blog/BlogPostList'
import ClientBar from '../components/ClientBar'
import { ActionButton, CTAAction } from '../components/HeroCTA'
import MeasurImage from '../components/MeasurImage'
import Metadata from '../components/Metadata'
import Page from '../components/Page'
import RichHero from '../components/RichHero'
import CollectionCardSection from '../components/section/CollectionCardSection'
import GlobalMapSection from '../components/section/GlobalMapSection'
import IllustratedSection from '../components/section/IllustratedSection'
import Section from '../components/section/Section'
import TridentSection from '../components/section/TridentSection'
import TeamMemberPageCard from '../components/TeamMemberPageCard'
import { LocaleContext, MeasurLocale } from '../locale/i18n'
import {
  blogPostUrl,
  staticPagePaths,
  staticPageSlugs,
} from '../locale/pathing'
import { filterLocale } from '../logic/contentful'
import { organizationMetadata } from '../logic/ld-json'
import {
  GatsbyImage,
  IContentfulBlogPost,
  IContentfulLanding,
  IContentfulTeamMember,
  Image,
} from '../logic/shared-types'
import { StaticPage } from '../logic/staticPage'

import './index.scss'

export interface IndexPageProps {
  data: {
    blogPosts: {
      nodes: IContentfulBlogPost[]
    }
    homePage: {
      blogDescription: string
      blogHeading: string
      caseStudiesHeading: string
      caseStudyBlogs: IContentfulBlogPost[]
      complianceBody: RenderRichTextData<never>
      complianceHeading: string
      compliancePicture: GatsbyImage
      globalServicesHeading: string
      globalServicesBody: RenderRichTextData<never>
      expertCards: IContentfulTeamMember[]
      expertsHeading: string
      experts: {
        experts: string
      }
      contentful_id: string
      node_locale: MeasurLocale
      howWeWorkBody: RenderRichTextData<never>
      howWeWorkHeading: string
      howWeWorkPicture: GatsbyImage
      metaTitle: string
      metaDescription: {
        metaDescription: string
      }
      heroTitle: string
      heroLeadText: {
        heroLeadText: string
      }
      heroImage: Image
      solutionCards: IContentfulLanding[]
      solutionsHeading: string
      valueProposition1: string
      valueProposition1Details: string
      valueProposition2: string
      valueProposition2Details: string
      valueProposition3: string
      valueProposition3Details: string
      valuePropositionHeading: string
    }
  }
}

const Index: React.FC<IndexPageProps> = ({ data: { blogPosts, homePage } }) => {
  const { locale, t } = useContext(LocaleContext)

  const localCaseStudies = filterLocale(homePage.caseStudyBlogs, locale).filter(
    b => b.whitelistedLocales?.includes(locale),
  )

  return (
    <Page localisedSlugs={staticPageSlugs(StaticPage.INDEX)}>
      <Metadata
        title={homePage.metaTitle}
        description={homePage.metaDescription.metaDescription}
        hideTitleSuffix
      >
        <script type="application/ld+json">
          {JSON.stringify(organizationMetadata)}
        </script>
      </Metadata>
      <div className="home">
        <RichHero
          heading={homePage.heroTitle}
          isMegaHero
          leadText={homePage.heroLeadText.heroLeadText}
          ctaAction={CTAAction.CONTACT_PAGE}
          ctaAction2={CTAAction.SHOP}
          trailingContent={
            <div className="home__heroTrailer">
              <ClientBar />
            </div>
          }
        >
          <picture>
            <source srcSet="/hero-photo.webp" type="image/webp" />
            <source srcSet="/hero-photo.jpg" type="image/jpeg" />
            <img
              className="rounded"
              src="/hero-photo.jpg"
              loading="lazy"
              alt="Scientist conducting test"
            />
          </picture>
        </RichHero>
        <TridentSection
          backgroundColor="light"
          heading={homePage.valuePropositionHeading}
          items={[
            {
              description: homePage.valueProposition1Details,
              heading: homePage.valueProposition1,
            },
            {
              description: homePage.valueProposition2Details,
              heading: homePage.valueProposition2,
            },
            {
              description: homePage.valueProposition3Details,
              heading: homePage.valueProposition3,
            },
          ]}
        />
        {localCaseStudies.length > 0 && (
          <Section
            backgroundColor="blue"
            mainTitle={homePage.caseStudiesHeading}
          >
            <div className="home__cases center">
              {localCaseStudies.map(
                ({ contentful_id, customerCaseStudyLogo, slug, title }) => (
                  <Link
                    className="home__case"
                    key={contentful_id}
                    to={blogPostUrl(locale, slug)}
                  >
                    {customerCaseStudyLogo?.gatsbyImageData ? (
                      <MeasurImage
                        image={customerCaseStudyLogo.gatsbyImageData}
                        objectFit="cover"
                        alt={customerCaseStudyLogo.title ?? ''}
                      />
                    ) : (
                      <img
                        alt={customerCaseStudyLogo?.title ?? ''}
                        src={customerCaseStudyLogo?.url}
                        loading="lazy"
                      />
                    )}
                    <h3>{title}</h3>
                  </Link>
                ),
              )}
            </div>
            <div className="ctaButtons">
              <ActionButton
                action={CTAAction.CONTACT_PAGE}
                role="primary"
                text={t('common.getStarted')}
              />
            </div>
          </Section>
        )}
        <IllustratedSection
          backgroundColor="light"
          mainTitle={homePage.howWeWorkHeading}
          image={homePage.howWeWorkPicture}
        >
          {renderRichText(homePage.howWeWorkBody)}
        </IllustratedSection>
        <IllustratedSection
          image={homePage.compliancePicture}
          mainTitle={homePage.complianceHeading}
          flip
        >
          {renderRichText(homePage.complianceBody)}
        </IllustratedSection>
        <GlobalMapSection
          backgroundColor="blue"
          mainTitle={homePage.globalServicesHeading}
        >
          {renderRichText(homePage.globalServicesBody)}
        </GlobalMapSection>
        <CollectionCardSection
          backgroundColor="light"
          cards={homePage.solutionCards.map(landingPage => ({
            ...landingPage,
            path: staticPagePaths[StaticPage.SOLUTIONS][locale],
          }))}
          mainTitle={homePage.solutionsHeading}
        >
          <div className="ctaButtons">
            <ActionButton action={CTAAction.SOLUTIONS} role="primary" />
          </div>
        </CollectionCardSection>
        <Section backgroundColor="blue" mainTitle={homePage.expertsHeading}>
          <p className="home__sectionBlurb">{homePage.experts.experts}</p>
          <div className="home__experts">
            {homePage.expertCards.map(member => (
              <TeamMemberPageCard key={member.slug} {...member} />
            ))}
          </div>
          <div className="ctaButtons">
            <ActionButton
              action={CTAAction.CONTACT_PAGE}
              role="primary"
              text={t('home.expertCta')}
            />
          </div>
        </Section>
        <Section mainTitle={homePage.blogHeading}>
          <BlogPostList posts={blogPosts.nodes} />
          <p className="home__sectionBlurb">{homePage.blogDescription}</p>
          <div className="ctaButtons">
            <ActionButton
              action={CTAAction.BLOG}
              role="primary"
              text={t('common.goToBlog')}
            />
          </div>
        </Section>
      </div>
    </Page>
  )
}

export const query = graphql`
  query ($locale: String!) {
    blogPosts: allContentfulBlogPost(
      filter: {
        node_locale: { eq: $locale }
        whitelistedLocales: { in: [$locale] }
      }
      limit: 3
      sort: { createdAt: DESC }
    ) {
      nodes {
        ...BlogPostPage
      }
    }
    homePage: contentfulHomePage(node_locale: { eq: $locale }) {
      contentful_id
      node_locale
      blogDescription
      blogHeading
      caseStudiesHeading
      caseStudyBlogs {
        ...BlogPostPage
        whitelistedLocales
      }
      complianceBody {
        raw
      }
      compliancePicture {
        contentful_id
        gatsbyImageData
        title
        url
      }
      complianceHeading
      expertCards {
        ...teamMemberPage
      }
      globalServicesHeading
      globalServicesBody {
        raw
      }
      expertsHeading
      experts {
        experts
      }
      howWeWorkHeading
      howWeWorkBody {
        raw
      }
      howWeWorkPicture {
        contentful_id
        gatsbyImageData
        title
        url
      }
      metaTitle
      metaDescription {
        metaDescription
      }
      heroTitle
      heroLeadText {
        heroLeadText
      }
      heroImage {
        title
        description
        url
        contentful_id
      }
      solutionCards {
        ...LandingPage
      }
      solutionsHeading
      valueProposition1
      valueProposition1Details
      valueProposition2
      valueProposition2Details
      valueProposition3
      valueProposition3Details
      valuePropositionHeading
    }
  }
`

export default Index
